import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminDashboard from "../AdminDashboard/admindash";
import {
  PlusCircle,
  MapPin,
  Clock,
  Briefcase,
  CheckCircle,
  DollarSign,
  ImageIcon,
  Plus,
  Trash2,
  Eye,
  Loader2,
  Navigation,
  Key,
  ShieldCheck,
  ShieldAlert,
  Plane,
  FileText
} from "lucide-react";
import { toast } from "react-toastify";
import API_URL from "../../config";


function InsertTravelPackage() {
  const [formData, setFormData] = useState({
    packageId: "",
    packageName: "",
    packageDescription: "",
    packageType: "",
    destinations: "",
    duration: "",
    startDate: "",
    endDate: "",
    itinerary: "",
    inclusions: "",
    exclusions: "",
    availability: true,
    currency: "LKR",
    price: "",
    transportType: "",
    travelImages: [""],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleImageChange = (index, value) => {
    const updated = [...formData.travelImages];
    updated[index] = value;
    setFormData({ ...formData, travelImages: updated });
  };

  const addImageField = () => {
    setFormData({ ...formData, travelImages: [...formData.travelImages, ""] });
  };

  const removeImageField = (index) => {
    const updated = formData.travelImages.filter((_, i) => i !== index);
    setFormData({ ...formData, travelImages: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        destinations: formData.destinations.split(",").map((s) => s.trim()).filter(s => s !== ""),
        itinerary: formData.itinerary.split(",").map((s) => s.trim()).filter(s => s !== ""),
        inclusions: formData.inclusions.split(",").map((s) => s.trim()).filter(s => s !== ""),
        exclusions: formData.exclusions.split(",").map((s) => s.trim()).filter(s => s !== ""),
        travelImages: formData.travelImages.filter((url) => url.trim() !== ""),
      };

      await axios.post(`${API_URL}/travelpackage`, payload);
      toast.success("Travel Package added successfully!");
      setTimeout(() => navigate("/admindash"), 1500);
    } catch (err) {
      console.error("Insert error:", err);
      toast.error(err.response?.data?.error || "Failed to add travel package.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminDashboard>
      <div className="font-sans">
        {/* Header Section */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-6">
          <div>
            <h1 className="text-2xl font-black text-sky-950 tracking-tight flex items-center gap-2">
              <PlusCircle className="text-sky-500" size={24} />
              New Adventure Package
            </h1>
            <p className="text-sm text-gray-400 font-bold mt-1">Design and publish a curated journey for TravelAura explorers.</p>
          </div>
          <button
            onClick={() => navigate("/admindash")}
            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-sky-600 transition-colors"
          >
            Discard Draft
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

          {/* Main Form Section */}
          <div className="lg:col-span-8">
            <form onSubmit={handleSubmit} className="space-y-10">

              {/* Section 1: Package Identity */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 border-l-4 border-sky-500 pl-4 py-1">
                  <h3 className="text-xs font-black text-sky-950 uppercase tracking-widest">Package Overview</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Package Name</label>
                    <div className="relative">
                      <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                      <input
                        name="packageName"
                        value={formData.packageName}
                        onChange={handleChange}
                        placeholder="e.g. Tropical Island Expedition"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Package ID / Code</label>
                    <div className="relative">
                      <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                      <input
                        name="packageId"
                        value={formData.packageId}
                        onChange={handleChange}
                        placeholder="e.g. PKG-TR-01"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Journey Type</label>
                    <div className="relative">
                      <Plane className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                      <select
                        name="packageType"
                        value={formData.packageType}
                        onChange={handleChange}
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none appearance-none text-sm"
                      >
                        <option value="" disabled>Select Type</option>
                        <option value="Adventure">Adventure</option>
                        <option value="Leisure">Leisure</option>
                        <option value="Cultural">Cultural</option>
                        <option value="Wellness">Wellness</option>
                        <option value="Wildlife">Wildlife</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Transport Mode</label>
                    <div className="relative">
                      <Navigation className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                      <input
                        name="transportType"
                        value={formData.transportType}
                        onChange={handleChange}
                        placeholder="e.g. Luxury Bus / Flight"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Package Narrative</label>
                  <div className="relative">
                    <FileText className="absolute left-4 top-4 text-sky-400" size={18} />
                    <textarea
                      name="packageDescription"
                      value={formData.packageDescription}
                      onChange={handleChange}
                      placeholder="Write a compelling story about this journey..."
                      required
                      rows="3"
                      className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none resize-none text-sm"
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Section 2: Timeline & Destinations */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 border-l-4 border-indigo-500 pl-4 py-1">
                  <h3 className="text-xs font-black text-sky-950 uppercase tracking-widest">Schedule & Path</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Journey Duration</label>
                    <div className="relative">
                      <Clock className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400" size={18} />
                      <input
                        name="duration"
                        value={formData.duration}
                        onChange={handleChange}
                        placeholder="e.g. 5 Days & 4 Nights"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-400 transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Start Date</label>
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-xs"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">End Date</label>
                      <input
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-xs"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Key Destinations (Comma Separated)</label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400" size={18} />
                    <input
                      name="destinations"
                      value={formData.destinations}
                      onChange={handleChange}
                      placeholder="e.g. Sigiriya, Kandy, Nuwara Eliya"
                      required
                      className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Itinerary & Logistics */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 border-l-4 border-amber-500 pl-4 py-1">
                  <h3 className="text-xs font-black text-sky-950 uppercase tracking-widest">Adventure Logistics</h3>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Detailed Itinerary (Steps Sep by Comma)</label>
                    <textarea
                      name="itinerary"
                      value={formData.itinerary}
                      onChange={handleChange}
                      placeholder="Day 1 Arrival, Day 2 Trekking, Day 3 Safari..."
                      required
                      rows="2"
                      className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none resize-none text-sm"
                    ></textarea>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-emerald-600 uppercase tracking-widest ml-1 flex items-center gap-1.5">
                        <ShieldCheck size={12} /> Package Inclusions
                      </label>
                      <textarea
                        name="inclusions"
                        value={formData.inclusions}
                        onChange={handleChange}
                        placeholder="Breakfast, AC Van, Tour Guide..."
                        required
                        rows="2"
                        className="w-full px-4 py-3.5 bg-emerald-50/30 border border-emerald-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none resize-none text-sm"
                      ></textarea>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-rose-600 uppercase tracking-widest ml-1 flex items-center gap-1.5">
                        <ShieldAlert size={12} /> Package Exclusions
                      </label>
                      <textarea
                        name="exclusions"
                        value={formData.exclusions}
                        onChange={handleChange}
                        placeholder="Personal Expenses, Visa, Gratuity..."
                        required
                        rows="2"
                        className="w-full px-4 py-3.5 bg-rose-50/30 border border-rose-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none resize-none text-sm"
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 4: Pricing & Media */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 border-l-4 border-emerald-500 pl-4 py-1">
                  <h3 className="text-xs font-black text-sky-950 uppercase tracking-widest">Pricing & Visuals</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Retail Price</label>
                    <div className="relative">
                      <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500" size={18} />
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        placeholder="0.00"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Currency</label>
                    <input
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                      placeholder="LKR"
                      required
                      className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                    />
                  </div>
                  <div className="flex flex-col justify-end pb-1.5">
                    <label className="flex items-center gap-3 bg-emerald-50/50 p-3.5 rounded-xl border border-emerald-100 cursor-pointer hover:bg-emerald-50 transition-all">
                      <input
                        type="checkbox"
                        name="availability"
                        checked={formData.availability}
                        onChange={handleChange}
                        className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-[10px] font-black text-emerald-900 uppercase tracking-widest">Active & Available</span>
                    </label>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Journey Gallery (Image URLs)</label>
                    <button
                      type="button"
                      onClick={addImageField}
                      className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-sky-600 hover:text-sky-700 transition-colors"
                    >
                      <Plus size={14} /> Add URL Field
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.travelImages.map((img, index) => (
                      <div key={index} className="flex gap-3">
                        <div className="relative flex-1">
                          <ImageIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={16} />
                          <input
                            type="text"
                            placeholder="Image URL"
                            value={img}
                            onChange={(e) => handleImageChange(index, e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-gray-50/30 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-xs"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeImageField(index)}
                          disabled={formData.travelImages.length === 1}
                          className="p-3 text-rose-300 hover:text-rose-500 transition-all disabled:opacity-0"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-5 bg-sky-600 text-white font-black rounded-2xl hover:bg-sky-700 active:scale-[0.98] transition-all shadow-xl shadow-sky-600/20 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed uppercase tracking-widest text-sm"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Publishing Adventure...
                    </>
                  ) : (
                    <>
                      Deploy Travel Package
                      <CheckCircle size={20} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-4 lg:sticky lg:top-8 self-start">
            <div className="flex items-center gap-2 mb-4 ml-2">
              <Eye size={16} className="text-sky-500" />
              <span className="text-[10px] font-black text-sky-950 uppercase tracking-widest">Live Experience Preview</span>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-[2.5rem] overflow-hidden group border-dashed p-2">
              <div className="bg-white rounded-[2rem] overflow-hidden shadow-sm border border-gray-50">
                <div className="relative h-48 bg-gray-100 overflow-hidden">
                  {formData.travelImages[0] ? (
                    <img
                      src={formData.travelImages[0]}
                      alt="Preview"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=1000&auto=format&fit=crop";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-2">
                      <ImageIcon size={32} />
                      <p className="text-[9px] font-black uppercase tracking-widest">Visual Preview</p>
                    </div>
                  )}
                  {formData.packageType && (
                    <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/95 backdrop-blur rounded-full text-[8px] font-black uppercase tracking-widest text-sky-600 shadow-xl shadow-black/5">
                      {formData.packageType}
                    </div>
                  )}
                  <div className="absolute bottom-4 right-4 bg-sky-500 text-white text-[8px] font-black px-2 py-1 rounded-md shadow-lg shadow-sky-500/20">
                    LIVE DRAFT
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-2 text-indigo-500 mb-2">
                    <Clock size={10} />
                    <span className="text-[8px] font-black uppercase tracking-widest">
                      {formData.duration || "Duration TBD"}
                    </span>
                  </div>
                  <h2 className="text-lg font-black text-sky-950 mb-3 truncate leading-tight">
                    {formData.packageName || "New Travel Package"}
                  </h2>

                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-xs font-black text-emerald-600">{formData.currency || "LKR"}</span>
                    <span className="text-xl font-black text-sky-950">{formData.price || "0"}</span>
                    <span className="text-[10px] text-gray-400 font-bold">/ person</span>
                  </div>

                  <p className="text-gray-400 text-[10px] font-bold leading-relaxed line-clamp-2 h-10">
                    {formData.packageDescription || "Enter a compelling description to see it appear here in the live preview window..."}
                  </p>

                  <div className="mt-6 pt-6 border-t border-gray-50 flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                      <span className="text-[7px] font-black text-gray-300 uppercase tracking-widest">Starting Port</span>
                      <span className="text-[9px] font-black text-sky-950">{formData.destinations.split(",")[0] || "TBD"}</span>
                    </div>
                    {formData.transportType && (
                      <div className="px-2 py-1 bg-sky-50 rounded text-[7px] font-black text-sky-600 uppercase">
                        {formData.transportType}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-sky-50 rounded-3xl p-6 border border-sky-100 shadow-sm relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-16 h-16 bg-sky-100/50 rounded-bl-[3rem] -mr-4 -mt-4 transition-all group-hover:scale-110"></div>
              <p className="text-[10px] font-bold text-sky-800 leading-relaxed italic relative z-10">
                "Travelers are 65% more likely to book packages with clear inclusions and beautiful imagery. Make it shine!"
              </p>
            </div>
          </div>

        </div>
      </div>
    </AdminDashboard>
  );
}

export default InsertTravelPackage;
